import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";

export type SearchKind = "book" | "activation" | "wisdom" | "music" | "song" | "academy";

export interface SearchHit {
  kind: SearchKind;
  slug: string;
  parent_slug: string | null;
  title: string;
  subtitle: string | null;
  rank: number;
}

const ALLOWED_KINDS: SearchKind[] = ["book", "activation", "wisdom", "music", "song", "academy"];

function makePublicClient() {
  const url = process.env.SUPABASE_URL!;
  const key = process.env.SUPABASE_PUBLISHABLE_KEY!;
  return createClient<Database>(url, key, {
    auth: { persistSession: false, autoRefreshToken: false },
    global: {
      fetch: (input, init) => {
        const h = new Headers(init?.headers);
        if (key.startsWith("sb_") && h.get("Authorization") === `Bearer ${key}`) h.delete("Authorization");
        h.set("apikey", key);
        return fetch(input, { ...init, headers: h });
      },
    },
  });
}

interface SearchInput {
  q: string;
  kinds?: SearchKind[];
  limit?: number;
}

export const searchTemple = createServerFn({ method: "POST" })
  .inputValidator((input: SearchInput) => {
    const q = typeof input?.q === "string" ? input.q.slice(0, 200) : "";
    const kinds = Array.isArray(input?.kinds)
      ? input!.kinds!.filter((k): k is SearchKind => ALLOWED_KINDS.includes(k))
      : undefined;
    const limit = typeof input?.limit === "number" ? Math.max(1, Math.min(input.limit, 100)) : 40;
    return { q, kinds, limit };
  })
  .handler(async ({ data }) => {
    const supabase = makePublicClient();
    const { data: rows, error } = await supabase.rpc("search_temple", {
      q: data.q,
      kinds: data.kinds && data.kinds.length ? data.kinds : undefined,
      lim: data.limit,
    });
    if (error) {
      // Fail soft — the client will fall back to its static index.
      return { hits: [] as SearchHit[], error: error.message };
    }
    return { hits: (rows ?? []) as SearchHit[], error: null };
  });
